import React from "react";
import { CloudOff, Languages, LockKeyhole, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { Link } from "react-router-dom";

const PROMISES = [
  {
    icon: CloudOff,
    title: "Keep the counter moving",
    body: "Core mobile sales and stock work can continue when the connection drops, then join the wider business record when service returns.",
  },
  {
    icon: RefreshCw,
    title: "One record across devices",
    body: "Work from the phone during the day and review the same business from the web workspace when you need a wider view.",
  },
  {
    icon: Languages,
    title: "Use familiar language",
    body: "Choose from nine supported languages and ask business questions without translating everything into technical terms.",
  },
  {
    icon: LockKeyhole,
    title: "Give access without giving up control",
    body: "Owners can define staff permissions, attribute activity to the right person, and keep sensitive actions protected.",
  },
];

export const Testimonials = () => (
  <section id="why-shopbook" data-testid={LANDING.testimonials} className="bg-[#F7FAF9] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-12">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">Product proof</span>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold leading-[1.1] text-[#0A2424] tracking-tight">
            Trust is not a slogan. <span className="italic text-[#0E6B6B]">It is how the record behaves.</span>
          </h2>
        </div>
        <p className="lg:col-span-5 text-lg leading-relaxed text-[#3a4a4a]">
          The system is designed to preserve context through weak connectivity, repeated sync attempts, staff handovers, branch changes, and balance-sensitive payments.
        </p>
      </div>

      <div className="mt-14 grid gap-5 md:grid-cols-2">
        {PROMISES.map(({ icon: Icon, title, body }) => (
          <article key={title} className="group rounded-3xl border border-[#0E6B6B]/8 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.03)] transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_rgb(0,0,0,0.06)]">
            <div className="flex items-start gap-5">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-[#0E6B6B]/8 text-[#0E6B6B] transition-colors group-hover:bg-[#0E6B6B] group-hover:text-white">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold text-[#0A2424]">{title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[#4B5E5E]">{body}</p>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-3xl bg-[#0A3B3B] p-7 text-white sm:flex-row sm:items-center sm:justify-between md:p-9">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10"><ShieldCheck className="h-6 w-6 text-[#FFD8A8]" /></div>
          <div>
            <div className="font-display text-xl font-semibold">Try the workflow before you commit.</div>
            <div className="mt-1 text-sm text-white/65">The free trial does not automatically charge you.</div>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#FFD8A8]"><Smartphone className="h-4 w-4" /> Web + mobile</div>
          <Link to="/contact?topic=customer-story" className="text-sm font-semibold text-white underline decoration-white/40 underline-offset-4 hover:decoration-white">Already using Shopbook? Share a result we can verify.</Link>
        </div>
      </div>
    </div>
  </section>
);

export default Testimonials;
